import React, { ReactNode, useEffect, useMemo } from 'react';
import cn from 'classnames';
import Container from '@components/ContentContainer';
import { ContactHeader } from '@repo/components/headers';
import Location from '@components/Contact/Location';
import Related from '@components/Contact/Related';
import { SourcesSection } from '@components/Contact/Sources';
import NetworkStrength from '@components/Contact/NetworkStrength';
import Properties from '@components/Contact/Properties';
import Groups from '@components/Contact/Groups';
import styles from './Sidebar.module.scss';
import Contact from '@repo/utils/models/Contact';
import { SharedContact } from '@repo/utils/models/SharedContact';
import { ContactInfo, ID } from '@repo/utils/types';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContactThrottled, selectContact } from '@repo/redux/actions/contacts';
import { useAuthContext } from '@repo/hooks';
import Highlights from '../Highlights';
import ReminderPreview, { ReminderPreviewProps } from './ReminderPreview';
import BirthdaySuggestionsBtn from './BirthdaySuggestionsBtn';
import SidebarTimeline from '../SidebarTimeline/SidebarTimeline';
import ViewMessage from '@components/ViewMessage';
import { CircledArrowIcon } from '@repo/icons';
import upgradeAction from '@support/upgrade-action';
import { ContactSource } from '@repo/redux/reducers/data/sources';
import { selectContactInteractionsSummary } from '@repo/redux/reducers/data/moments';

interface SidebarProps {
  className?: string;
  contact?: Contact | SharedContact;
  includeTimeline?: boolean;
  summary?: string[];
  reminder?: ReminderPreviewProps;
  sources?: ContactInfo[];
  isShared?: boolean;
  children?: ReactNode;
}

const ContactSidebarContent = ({
  contact,
  includeTimeline,
  summary,
  sources,
  isShared,
  children,
}: SidebarProps) => {
  const { contactIsRestricted } = useAuthContext();

  return (
    <>
      {children}
      {!contactIsRestricted(contact) && (
        <>
          <NetworkStrength contact={contact} />
          {includeTimeline && <SidebarTimeline contact={contact} />}
          <Related contacts={contact.related} />
          <Properties contact={contact} />
          <SourcesSection
            contactID={contact?.id}
            summary={summary}
            sources={sources}
            isShared={isShared}
          />
          <Groups contact={contact} />
        </>
      )}
    </>
  );
};

const ContactSidebarLocation = ({ contact }: { contact: Contact | SharedContact }) => {
  const { contactIsRestricted } = useAuthContext();

  return <>{!contactIsRestricted(contact) && <Location contact={contact} />}</>;
};

function RestrictedContactSidebar({ contact }: { contact: Contact }) {
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const placeholderContact = useMemo(
    () =>
      contact.copyWith({
        related: [
          new Contact({ displayName: 'George Costanza', id: user.contact_id }),
          new Contact({ displayName: 'Elaine Benes', id: user.contact_id }),
        ],
      }),
    [],
  );

  return (
    <>
      <div className={styles.sidebar}>
        <div className={styles.sidebarPrimary}>
          <ContactHeader sidebar contact={contact} />
          <div className={styles.placeholderProfile}>
            <Related contacts={placeholderContact.related} />
            <SourcesSection
              sources={[
                { type: 'email', value: 'lorem@ipsum.com' } as ContactSource,
                { type: 'linkedin', value: 'lorem_ipsum' } as ContactSource,
                { source: 'CL', value: 'lorem@ipsum.com' } as ContactSource,
              ]}
              summary={[
                "You've lorem ipsumed with this person 3 times, most recently 3 months ago. You've also exchanged 80 emails and 40 text messages.",
              ]}
            />
          </div>
        </div>
        <ViewMessage
          className={styles.upgradeOverlay}
          underHeader
          icon={<CircledArrowIcon width="22" height="22" color="textPrimary" />}
          headline="Upgrade your account"
          message={
            <>
              You've reached the limit of relationships you can view on the free plan. Upgrade to
              view {contact.displayName}, take notes, set reminders, and more.
            </>
          }
          button={{
            text: 'Upgrade Account',
            onClick: upgradeAction(user, dispatch),
          }}
        />
      </div>
    </>
  );
}

// Common component for plopping a Contact into a Sidebar. Will render any children if no
// Contact is passed in.
function ContactSidebarDisplay({
  className,
  contact,
  includeTimeline,
  summary,
  reminder,
  children,
  includeHeader,
  includeHighlights,
  includeBirthdaySuggestions,
  sources,
  isShared,
}: SidebarProps & {
  includeHeader?: boolean;
  includeHighlights?: boolean;
  includeBirthdaySuggestions?: boolean;
}) {
  const { contactIsRestricted } = useAuthContext();
  const isRestricted = contact && contactIsRestricted(contact);

  return (
    <Container.Sidebar
      className={cn(styles.sidebarContent, includeHeader && styles.withHeader, className)}
    >
      {!contact && children}
      {contact && !isRestricted && (
        <div className={styles.sidebar}>
          <div className={styles.sidebarPrimary}>
            {includeHeader && <ContactHeader sidebar contact={contact} />}
            {reminder && <ReminderPreview key={String(reminder.date?.unix)} reminder={reminder} />}
            {includeHighlights && <Highlights contactID={contact.id} />}
            {includeBirthdaySuggestions && <BirthdaySuggestionsBtn contact={contact} />}
            <ContactSidebarContent
              contact={contact}
              includeTimeline={includeTimeline}
              summary={summary}
              sources={sources}
              isShared={isShared}
            />
          </div>
          <ContactSidebarLocation contact={contact} />
        </div>
      )}

      {isRestricted && includeHeader ? <RestrictedContactSidebar contact={contact} /> : null}
    </Container.Sidebar>
  );
}

// Dumb component for Contact preview Sidebars
export function ContactPreviewSidebarDisplay({
  className,
  contact,
  summary,
  reminder,
  children,
  includeHighlights,
  includeBirthdaySuggestions,
  sources,
  isShared,
}: SidebarProps & { includeHighlights?: boolean; includeBirthdaySuggestions?: boolean }) {
  return (
    <ContactSidebarDisplay
      key={contact?.id}
      className={className}
      contact={contact}
      summary={summary}
      reminder={reminder}
      sources={sources}
      children={children}
      includeTimeline
      includeHeader
      includeHighlights={includeHighlights}
      includeBirthdaySuggestions={includeBirthdaySuggestions}
      isShared={isShared}
    />
  );
}

// Dumb component for any Contact view Sidebar
export function ContactViewSidebarDisplay({
  className,
  contact,
  summary,
  sources,
  children,
}: SidebarProps) {
  return (
    <ContactSidebarDisplay
      className={className}
      contact={contact}
      summary={summary}
      children={children}
      includeHeader={false}
      sources={sources}
    />
  );
}

// Connected component that will render a preview for a passed
// in Contact ID (or children if there are any)
export function ContactPreviewSidebar({
  contactID,
  reminder,
  includeHighlights,
  includeBirthdaySuggestions,
  children,
  nonContactPreviewClassName,
}: {
  contactID?: ID;
  reminder?: ReminderPreviewProps;
  includeHighlights?: boolean;
  includeBirthdaySuggestions?: boolean;
  children?: ReactNode;
  nonContactPreviewClassName?: string;
}) {
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const isSelf = user?.contact_id === Number(contactID);
  const contact = useSelector(selectContact(contactID, null));
  const summary = useSelector(selectContactInteractionsSummary(contactID));

  useEffect(() => {
    if (contactID) {
      dispatch(fetchContactThrottled({ id: contactID }));
    }
  }, [contactID]);

  if ((!contactID || !contact) && !children) {
    return <Container.Sidebar />;
  }

  if (!contactID && children) {
    return <Container.Sidebar className={nonContactPreviewClassName}>{children}</Container.Sidebar>;
  }

  return contact ? (
    <ContactPreviewSidebarDisplay
      key={contactID}
      contact={contactID && contact}
      summary={!isSelf && summary}
      reminder={reminder}
      includeHighlights={includeHighlights}
      includeBirthdaySuggestions={includeBirthdaySuggestions}
      children={children}
    />
  ) : (
    <>{children}</>
  );
}
